import { INITIAL_SKILL_TREE_VIEW, type SkillTreeViewStore } from '@sorrem-client/ui/features/SkillTree';
import { createValueStore } from '@sorrem-client/ui/shared/value-store';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** How long the page and the skill tree wait for the sky before showing anyway (a failed or very slow sky). */
const SKILL_SKY_READY_FALLBACK_MS = 2500;

export interface SkillsTabScene {
    /** The pan/zoom view the tree, the sky and the zoom buttons share; one store for the page's life. */
    viewStore: SkillTreeViewStore;
    /** The tree mounts on the first visit to the tab and then stays loaded, so switching back is instant. */
    hasVisitedSkills: boolean;
    /** The sky has drawn (or the fallback ran out). */
    isSkyReady: boolean;
    onSkyReady: () => void;
    /** The tree is on screen, including while it shrinks back into the sigil. */
    setIsTreeShown: (shown: boolean) => void;
    /** The sky and the home's window zoom stay up while Skills shows or the tree is still leaving. */
    isBackdropShown: boolean;
    /** The view has zoomed through the window into the sky; the tree grows only then. */
    isZoomedIntoWindow: boolean;
    setIsZoomedIntoWindow: (zoomedIn: boolean) => void;
    /** The main region's bounds: the tree is full screen but opens framed in them. */
    mainRegionRef: RefObject<HTMLDivElement>;
}

/**
 * The Skills tab's scene. The Veil sky is always behind the home, showing
 * through its window; the Skills tab zooms through that window into it. The
 * tree (center panel) and the sky share one pan/zoom view. The page and the
 * tree wait for the sky, so neither shows an empty window or background; a
 * fallback lets them show if it never reports in.
 */
export function useSkillsTabScene(isSkillsTab: boolean): SkillsTabScene {
    const [viewStore] = useState(() => createValueStore(INITIAL_SKILL_TREE_VIEW));
    const [hasVisitedSkills, setHasVisitedSkills] = useState(false);
    const mainRegionRef = useRef<HTMLDivElement>(null);
    // Set while rendering, so the first frame on the tab already has the tree (#330).
    if (isSkillsTab && !hasVisitedSkills) {
        setHasVisitedSkills(true);
    }

    const [isSkyReady, setIsSkyReady] = useState(false);
    const onSkyReady = useCallback((): void => setIsSkyReady(true), []);
    useEffect(() => {
        if (isSkyReady) return;
        const timer = window.setTimeout(() => setIsSkyReady(true), SKILL_SKY_READY_FALLBACK_MS);
        return () => window.clearTimeout(timer);
    }, [isSkyReady]);

    // The tree stays on screen while it shrinks back into the sigil; the sky and the character wait for it.
    const [isTreeShown, setIsTreeShown] = useState(false);
    const [isZoomedIntoWindow, setIsZoomedIntoWindow] = useState(false);

    return {
        viewStore,
        hasVisitedSkills,
        isSkyReady,
        onSkyReady,
        setIsTreeShown,
        isBackdropShown: isSkillsTab || isTreeShown,
        isZoomedIntoWindow,
        setIsZoomedIntoWindow,
        mainRegionRef,
    };
}
